import {
    DatePicker,
    Drawer,
    Input,
    InputNumber,
    Radio,
    Select,
    Switch,
} from 'antd';
import { Button } from '../../../components/button';
import { getIn, useFormik } from 'formik';
import { observer } from 'mobx-react';
import moment from 'moment-timezone';
import React, { useEffect, useMemo } from 'react';
import { LabeledField } from '../../../components/form-fields/labeled-field';
import { Section } from '../../../components/form-fields/section';
import { ImageInput } from '../../../components/image-input';
import { BusinessUnitsSelectTree } from '../../../components/region-selectors/business-units-select-tree';
import {
    useBusinessUnitStore,
    useCategoryStore,
    useDoc31Store,
} from '../../../contexts/mobx';
import { Doc31WriteResult } from '../../../stores/doc31';
import { Doc31CouponPromotion } from '../../../types/doc31';
import { DOC31_COUPON_STATUS_LABELS } from '../../../utils/doc31';
import { cleanProvinceIds } from '../../../utils/rpp-prize-coupon';
import { Doc31ReasonPrompt } from '../reason-prompt';
import {
    CouponForm,
    CouponFormSchema,
    PERCENT_MIN,
    CouponLanguage,
    buildCouponPatch,
    couponToForm,
    createCouponForm,
    formToBody,
    instantToPicker,
    LOCAL_DATE,
    drawShare,
    wallClockToInstant,
} from './form';
import { PacingEditor } from './pacing-editor';
import style from './style.module.scss';

const DATE_TIME_FORMAT = 'MMM D, YYYY HH:mm';
const WITHDRAW_LABEL = 'Withdraw from the draw';

type FieldName = keyof CouponForm;

interface CouponDrawerProps {
    /** The coupon being edited; null creates one. */
    coupon: Doc31CouponPromotion | null;
    timezone: string;
    /** Weight of the other coupons in the draw, for the share preview. */
    otherDrawWeight: number;
    prompt: Doc31ReasonPrompt['prompt'];
    onClose: () => void;
}

export const CouponDrawer = observer(
    ({
        coupon,
        timezone,
        otherDrawWeight,
        prompt,
        onClose,
    }: CouponDrawerProps) => {
        const store = useDoc31Store();
        const categoryStore = useCategoryStore();
        const businessUnitStore = useBusinessUnitStore();

        useEffect(() => {
            categoryStore.fetchActiveCategories();
            businessUnitStore.fetchBusinessUnits();
        }, [categoryStore, businessUnitStore]);

        const edition = store.view?.configuration.edition;
        const initialValues = useMemo(
            () =>
                coupon
                    ? couponToForm(coupon)
                    : createCouponForm({
                          startDate: edition?.playStartsAt ?? null,
                          endDate:
                              edition?.redemptionEndsAt ??
                              edition?.playEndsAt ??
                              null,
                      }),
            // The form starts from what was saved when the drawer opened.
            // eslint-disable-next-line react-hooks/exhaustive-deps
            []
        );

        const isWithdrawn = coupon?.selection.status === 'withdrawn';
        const readOnly = store.isReadOnly || isWithdrawn;

        const settle = (result: Doc31WriteResult<Doc31CouponPromotion>) => {
            if (result.ok) {
                onClose();
            }
        };

        const formik = useFormik<CouponForm>({
            initialValues,
            validationSchema: CouponFormSchema,
            onSubmit: async (values) => {
                if (!coupon) {
                    settle(await store.createCoupon(formToBody(values)));
                    return;
                }
                const patch = buildCouponPatch(coupon, values);
                if (Object.keys(patch).length === 0) {
                    return;
                }
                settle(await store.updateCoupon(coupon.promotionId, patch));
            },
        });

        const { values, setFieldValue } = formik;
        const hasChanges =
            !coupon || Object.keys(buildCouponPatch(coupon, values)).length > 0;

        const withdraw = async () => {
            if (!coupon) {
                return;
            }
            const reason = await prompt({
                actionLabel: WITHDRAW_LABEL,
                description:
                    'It leaves the draw for good. Coupons already won keep working.',
                danger: true,
            });
            if (reason) {
                settle(await store.withdrawCoupon(coupon.promotionId, reason));
            }
        };

        /** A labeled control; `errorName` is the formik path of its value. */
        const field = (
            name: FieldName,
            label: string,
            render: (disabled: boolean) => React.ReactNode,
            options: { errorName?: string; helperText?: string } = {}
        ) => {
            const formError: unknown =
                formik.submitCount > 0
                    ? getIn(formik.errors, options.errorName ?? name)
                    : undefined;
            const error = typeof formError === 'string' ? formError : undefined;
            return (
                <LabeledField
                    label={label}
                    hasError={!!error}
                    helperText={error ?? options.helperText}
                >
                    {render(readOnly)}
                </LabeledField>
            );
        };

        const numberField = (
            name: FieldName,
            label: string,
            options: {
                min?: number;
                max?: number;
                precision?: number;
                placeholder?: string;
                helperText?: string;
            } = {}
        ) =>
            field(
                name,
                label,
                (disabled) => (
                    <InputNumber
                        aria-label={label}
                        min={options.min ?? 0}
                        max={options.max}
                        precision={options.precision}
                        placeholder={options.placeholder}
                        disabled={disabled}
                        value={values[name] as number | null}
                        onChange={(value) =>
                            setFieldValue(
                                name,
                                typeof value === 'number' ? value : null
                            )
                        }
                    />
                ),
                { helperText: options.helperText }
            );

        const imageField = (
            language: CouponLanguage,
            key: 'thumbnail' | 'background',
            label: string
        ) =>
            field('items', label, (disabled) => {
                const url = values.items[language][key];
                if (disabled) {
                    return url ? (
                        <img className={style.image_preview} src={url} alt="" />
                    ) : (
                        <span className={style.muted}>—</span>
                    );
                }
                return (
                    <ImageInput
                        url={url}
                        onChange={(next) =>
                            setFieldValue(`items.${language}.${key}`, next)
                        }
                        onDelete={() =>
                            setFieldValue(`items.${language}.${key}`, null)
                        }
                    />
                );
            });

        const copyColumn = (
            language: CouponLanguage,
            languageLabel: string
        ) => (
            <div key={language} className="column">
                <span className="h5_bold">{languageLabel} *</span>
                <div className={style.images}>
                    {imageField(
                        language,
                        'thumbnail',
                        `Thumbnail (${language.toUpperCase()})`
                    )}
                    {imageField(
                        language,
                        'background',
                        `Background (${language.toUpperCase()})`
                    )}
                </div>
                {field(
                    'items',
                    `Title (${languageLabel})`,
                    (disabled) => (
                        <Input
                            aria-label={`${languageLabel} title`}
                            disabled={disabled}
                            value={values.items[language].title}
                            onChange={(event) =>
                                setFieldValue(
                                    `items.${language}.title`,
                                    event.target.value
                                )
                            }
                        />
                    ),
                    { errorName: `items.${language}.title` }
                )}
                {field('items', `Subtitle (${languageLabel})`, (disabled) => (
                    <Input
                        aria-label={`${languageLabel} subtitle`}
                        disabled={disabled}
                        value={values.items[language].subTitle}
                        onChange={(event) =>
                            setFieldValue(
                                `items.${language}.subTitle`,
                                event.target.value
                            )
                        }
                    />
                ))}
                {field(
                    'items',
                    `Description (${languageLabel})`,
                    (disabled) => (
                        <Input.TextArea
                            aria-label={`${languageLabel} description`}
                            disabled={disabled}
                            autoSize={{ minRows: 2, maxRows: 6 }}
                            value={values.items[language].description}
                            onChange={(event) =>
                                setFieldValue(
                                    `items.${language}.description`,
                                    event.target.value
                                )
                            }
                        />
                    )
                )}
            </div>
        );

        const dateField = (name: 'startDate' | 'endDate', label: string) =>
            field(
                name,
                label,
                (disabled) => (
                    <DatePicker
                        aria-label={label}
                        showNow={false}
                        showTime={{ format: 'HH:mm' }}
                        format={DATE_TIME_FORMAT}
                        allowClear={false}
                        disabled={disabled}
                        value={instantToPicker(values[name], timezone)}
                        onChange={(picked) =>
                            setFieldValue(
                                name,
                                picked
                                    ? wallClockToInstant(picked, timezone)
                                    : null
                            )
                        }
                    />
                ),
                { helperText: `In ${timezone}.` }
            );

        const statusOptions = [
            { label: DOC31_COUPON_STATUS_LABELS.draft, value: 'draft' },
            { label: DOC31_COUPON_STATUS_LABELS.active, value: 'active' },
            ...(isWithdrawn
                ? [
                      {
                          label: DOC31_COUPON_STATUS_LABELS.withdrawn,
                          value: 'withdrawn',
                      },
                  ]
                : []),
        ];

        const sharePreview =
            values.status === 'active' && values.weight !== null
                ? drawShare(values.weight, otherDrawWeight + values.weight)
                : null;

        const provinces = businessUnitStore.businessUnits?.flatMap(
            (unit) => unit.provinces
        );
        const provincesText = provinces
            ? values.provinceIds
                  .map(
                      (id) =>
                          provinces.find((province) => province.id === id)
                              ?.name ?? `#${id}`
                  )
                  .join(', ')
            : `${values.provinceIds.length} provinces`;

        const firstPacingDate = moment
            .tz(edition?.playStartsAt ?? moment().toISOString(), timezone)
            .format(LOCAL_DATE);

        const footer = readOnly ? null : (
            <div className={style.drawer_footer}>
                {coupon && !isWithdrawn ? (
                    <Button
                        variant="secondary-destructive"
                        onClick={withdraw}
                        disabled={store.isSaving}
                    >
                        {WITHDRAW_LABEL}
                    </Button>
                ) : (
                    <span />
                )}
                <div className={style.footer_actions}>
                    <Button variant="secondary" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        loading={store.isSaving || formik.isSubmitting}
                        disabled={!hasChanges}
                        onClick={() => formik.submitForm()}
                    >
                        {coupon ? 'Save coupon' : 'Create coupon'}
                    </Button>
                </div>
            </div>
        );

        const title = coupon
            ? coupon.items.find((item) => item.language === 'en')?.title ??
              `Coupon ${coupon.promotionId}`
            : 'New coupon';

        return (
            <Drawer
                open
                width={640}
                title={title}
                onClose={onClose}
                maskClosable={!formik.dirty}
                footer={footer}
                className={style.drawer}
            >
                {isWithdrawn && (
                    <p className={style.note}>
                        Withdrawn coupons can’t change. Add a new one instead.
                    </p>
                )}
                <Section title="Copy">
                    <div className="columns_layout">
                        {copyColumn('en', 'English')}
                        {copyColumn('fr', 'French')}
                    </div>
                </Section>

                <Section title="Stock and dates">
                    <div className={style.fields}>
                        {numberField('quantity', 'Quantity', {
                            min: 1,
                            precision: 0,
                            helperText: coupon
                                ? `${coupon.issuedQuantity.toLocaleString()} issued so far.`
                                : undefined,
                        })}
                        <div className={style.pair}>
                            {dateField('startDate', 'Start')}
                            {dateField('endDate', 'End')}
                        </div>
                        {field('isActive', 'Active', (disabled) => (
                            <Switch
                                aria-label="Active"
                                className={style.switch}
                                disabled={disabled}
                                checked={values.isActive}
                                onChange={(checked) =>
                                    setFieldValue('isActive', checked)
                                }
                            />
                        ))}
                        {field('provinceIds', 'Provinces', (disabled) =>
                            disabled ? (
                                <span>{provincesText || '—'}</span>
                            ) : (
                                <BusinessUnitsSelectTree
                                    value={values.provinceIds}
                                    onChange={(ids) =>
                                        setFieldValue(
                                            'provinceIds',
                                            cleanProvinceIds(ids)
                                        )
                                    }
                                />
                            )
                        )}
                        {field('categoryId', 'Category', (disabled) => (
                            <Select
                                aria-label="Category"
                                placeholder="Pick a category"
                                disabled={disabled}
                                loading={
                                    categoryStore.isFetchingActiveCategories
                                }
                                value={values.categoryId ?? undefined}
                                onChange={(id: number) =>
                                    setFieldValue('categoryId', id)
                                }
                                options={(
                                    categoryStore.activeCategories?.results ??
                                    []
                                ).map((category) => ({
                                    value: category.id,
                                    label:
                                        category.items[0]?.title ??
                                        `#${category.id}`,
                                }))}
                            />
                        ))}
                        {field(
                            'activationCode',
                            'Activation code',
                            (disabled) => (
                                <Input
                                    aria-label="Activation code"
                                    inputMode="numeric"
                                    disabled={disabled}
                                    value={values.activationCode}
                                    suffix={
                                        <span className={style.muted}>
                                            {values.activationCode.length}/12
                                        </span>
                                    }
                                    onChange={(event) =>
                                        setFieldValue(
                                            'activationCode',
                                            event.target.value
                                                .replace(/\D/g, '')
                                                .slice(0, 12)
                                        )
                                    }
                                />
                            )
                        )}
                        <div className={style.triple}>
                            {numberField('discountValue', 'Discount value', {
                                placeholder: 'None',
                            })}
                            {numberField('rebateAmount', 'Rebate amount')}
                            {numberField('originalPrice', 'Original price')}
                        </div>
                    </div>
                </Section>

                <Section title="Odds and pacing">
                    <div className={style.fields}>
                        {field(
                            'status',
                            'Draw status',
                            (disabled) => (
                                <Radio.Group
                                    optionType="button"
                                    buttonStyle="solid"
                                    disabled={disabled}
                                    options={statusOptions}
                                    value={values.status}
                                    onChange={(event) =>
                                        setFieldValue(
                                            'status',
                                            event.target.value
                                        )
                                    }
                                />
                            ),
                            {
                                helperText:
                                    'Only coupons in the draw can be won.',
                            }
                        )}
                        {numberField('weight', 'Odds weight', {
                            helperText:
                                sharePreview === null
                                    ? 'Counts once the coupon is in the draw.'
                                    : `${sharePreview} of the draw.`,
                        })}
                        {field(
                            'pacingTargets',
                            'Pacing',
                            (disabled) => (
                                <PacingEditor
                                    periods={values.pacingTargets}
                                    quantity={values.quantity}
                                    firstDate={firstPacingDate}
                                    disabled={disabled}
                                    errors={
                                        formik.submitCount > 0
                                            ? formik.errors.pacingTargets
                                            : undefined
                                    }
                                    onChange={(periods) =>
                                        setFieldValue('pacingTargets', periods)
                                    }
                                />
                            ),
                            {
                                helperText:
                                    'Wins to aim for in each period, in edition dates.',
                            }
                        )}
                        <div className={style.pair}>
                            {numberField(
                                'maxWinsPerParticipation',
                                'Max wins per player',
                                {
                                    min: 1,
                                    precision: 0,
                                    placeholder: 'No limit',
                                }
                            )}
                            {numberField('minimumAge', 'Minimum age', {
                                precision: 0,
                                placeholder: 'None',
                            })}
                        </div>
                        <div className={style.pair}>
                            {numberField(
                                'alertUtilizationPercent',
                                'Stock alert %',
                                {
                                    min: PERCENT_MIN,
                                    max: 100,
                                    helperText:
                                        'Warns when this much is issued.',
                                }
                            )}
                            {numberField(
                                'servingUtilizationPercent',
                                'Serving limit %',
                                {
                                    min: PERCENT_MIN,
                                    max: 100,
                                    placeholder: 'Full stock',
                                    helperText:
                                        'Stops awarding at this share. Empty serves the full stock.',
                                }
                            )}
                        </div>
                    </div>
                </Section>
            </Drawer>
        );
    }
);
